import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { sectionRenderer } from '@theme/section-renderer';
import { debounce } from '@theme/utilities';

/**
 * Client-side filtering for the facets that are derived from the product title.
 *
 * The store uses an app for product options instead of Shopify options, so the Search & Discovery
 * app can't build these facets. The values are computed in Liquid and rendered onto every card
 * (see `snippets/util-title-facet-attributes.liquid`); this module matches them against the URL.
 *
 * Because Liquid can't read arbitrary query parameters, the filtering has to happen in the browser.
 * When a title facet is active the whole result set is pulled in through the Section Rendering API
 * so the filter applies across every page instead of only the page currently rendered.
 */

/** URL parameter -> the `dataset` key holding the matching value on a card. */
const TITLE_FACETS = {
  'product-type': 'productType',
  'print-type': 'printType',
  material: 'material',
  color: 'color',
};

const PARAM_NAMES = Object.keys(TITLE_FACETS);
const HIDDEN_CLASS = 'title-facet-hidden';
const ACTIVE_CLASS = 'title-facets-active';
const CARD_SELECTOR = ':scope > .product-grid__item';

/**
 * @typedef {Record<string, Set<string>>} ActiveValues
 */

class TitleFacets {
  /** Guards against reacting to our own DOM mutations. */
  #busy = false;

  /** Serialises the async work so concurrent calls can't append the same page twice. */
  #queue = Promise.resolve();

  /** @type {MutationObserver | undefined} */
  #renderObserver;

  constructor() {
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
    window.addEventListener('popstate', () => this.scheduleSync());
  }

  /**
   * Restores the inputs from the URL and re-applies the filtering to the grid.
   */
  scheduleSync = debounce(() => {
    this.#enqueue(async () => {
      this.#restoreInputs();
      this.#updateSummaries();
      await this.#applyToGrid();
    });
  }, 50);

  /**
   * Mirrors a change across every copy of the filter UI, writes the URL, and filters the grid.
   *
   * @param {TitleFacetInputs} source - The component the change originated from
   */
  commit(source) {
    this.#mirrorInputs(source);
    this.#writeUrl();
    this.#updateSummaries();
    this.#enqueue(() => this.#applyToGrid());
  }

  /**
   * @param {() => Promise<void>} task - The task to run once the previous one settles
   */
  #enqueue(task) {
    this.#queue = this.#queue.then(task).catch((error) => console.error(error));
  }

  /**
   * Hides the cards that don't match the active values, loading the remaining pages first.
   */
  async #applyToGrid() {
    const grid = this.#grid;
    if (!grid) return;

    const active = this.#activeValues();
    const resultsList = this.#resultsList;

    if (!PARAM_NAMES.some((param) => active[param]?.size)) {
      this.#busy = true;
      resultsList?.classList.remove(ACTIVE_CLASS);
      for (const card of grid.querySelectorAll(`.${HIDDEN_CLASS}`)) card.classList.remove(HIDDEN_CLASS);
      for (const card of grid.querySelectorAll('[data-title-facet-injected]')) card.remove();
      delete grid.dataset.titleFacetPagesLoaded;
      this.#renderCount(null);
      this.#renderEmptyState(false);
      this.#release();
      return;
    }

    this.#busy = true;
    resultsList?.classList.add(ACTIVE_CLASS);

    try {
      await this.#loadRemainingPages(grid);

      let visible = 0;
      for (const card of grid.querySelectorAll(CARD_SELECTOR)) {
        if (!(card instanceof HTMLElement)) continue;

        const matches = PARAM_NAMES.every((param) => {
          const wanted = active[param];
          if (!wanted?.size) return true;
          return wanted.has(card.dataset[TITLE_FACETS[param]] ?? '');
        });

        card.classList.toggle(HIDDEN_CLASS, !matches);
        if (matches) visible++;
      }

      this.#renderCount(visible);
      this.#renderEmptyState(visible === 0);
    } finally {
      this.#release();
    }
  }

  /**
   * Appends every page of the current result set to the grid, in page order.
   *
   * @param {HTMLElement} grid - The product grid
   */
  async #loadRemainingPages(grid) {
    const lastPage = Number(grid.dataset.lastPage) || 1;
    if (lastPage <= 1 || grid.dataset.titleFacetPagesLoaded === String(lastPage)) return;

    const sectionId = this.#resultsList?.getAttribute('section-id');
    if (!sectionId) return;

    const rendered = Array.from(grid.querySelectorAll(CARD_SELECTOR));
    const currentPage = Number(/** @type {HTMLElement | undefined} */ (rendered[0])?.dataset.page) || 1;
    const seen = new Set(
      rendered.map((card) => (card instanceof HTMLElement ? card.dataset.productId : undefined)).filter(Boolean)
    );

    /** @type {Map<number, Element[]>} */
    const byPage = new Map([[currentPage, rendered]]);

    const pages = [];
    for (let page = 1; page <= lastPage; page++) {
      if (page !== currentPage) pages.push(page);
    }

    const results = await Promise.all(pages.map((page) => this.#fetchPage(sectionId, page)));

    for (const { page, cards } of results) {
      const fresh = cards.filter((card) => {
        const id = card instanceof HTMLElement ? card.dataset.productId : undefined;
        if (!id || seen.has(id)) return false;
        seen.add(id);
        card.setAttribute('data-title-facet-injected', '');
        return true;
      });

      byPage.set(page, fresh);
    }

    /** @type {Element[]} */
    const ordered = [];
    for (let page = 1; page <= lastPage; page++) ordered.push(...(byPage.get(page) ?? []));

    // `append` moves the already-rendered cards, so the grid ends up in page order.
    grid.append(...ordered);
    grid.dataset.titleFacetPagesLoaded = String(lastPage);
  }

  /**
   * @param {string} sectionId - The section to render
   * @param {number} page - The page to fetch
   * @returns {Promise<{ page: number, cards: Element[] }>}
   */
  async #fetchPage(sectionId, page) {
    const url = new URL(window.location.href);
    url.hash = '';
    url.searchParams.set('page', String(page));
    // Keep the cache key stable across title facet changes — the server ignores these anyway.
    for (const param of PARAM_NAMES) url.searchParams.delete(param);

    try {
      const html = await sectionRenderer.getSectionHTML(sectionId, true, url);
      const grid = new DOMParser().parseFromString(html, 'text/html').querySelector('[ref="grid"]');

      return { page, cards: grid ? Array.from(grid.querySelectorAll(CARD_SELECTOR)) : [] };
    } catch {
      return { page, cards: [] };
    }
  }

  /**
   * Reads the active values from the URL.
   *
   * @returns {ActiveValues}
   */
  #activeValues() {
    const params = new URLSearchParams(window.location.search);

    /** @type {ActiveValues} */
    const active = {};
    for (const param of PARAM_NAMES) active[param] = new Set(params.getAll(param).filter(Boolean));

    return active;
  }

  /**
   * Writes the checked values to the URL. Deliberately does not trigger a section render — the
   * server can't act on these parameters, so re-rendering would only throw the results away.
   */
  #writeUrl() {
    const url = new URL(window.location.href);

    for (const param of PARAM_NAMES) {
      url.searchParams.delete(param);
      for (const value of this.#checkedValues(param)) url.searchParams.append(param, value);
    }

    url.searchParams.delete('page');
    history.pushState({}, '', url.toString());
  }

  /**
   * @param {string} param - The filter parameter name
   * @returns {Set<string>} The checked values across every copy of the filter UI
   */
  #checkedValues(param) {
    const inputs = document.querySelectorAll(`title-facet-inputs input[name="${CSS.escape(param)}"]:checked`);

    return new Set(Array.from(inputs, (input) => /** @type {HTMLInputElement} */ (input).value));
  }

  /**
   * Applies a component's checked state to the other copies of the same filter (inline vs. drawer).
   *
   * @param {TitleFacetInputs} source - The component the change originated from
   */
  #mirrorInputs(source) {
    for (const input of source.querySelectorAll('input[type="checkbox"]')) {
      if (!(input instanceof HTMLInputElement)) continue;

      const selector = `title-facet-inputs input[name="${CSS.escape(input.name)}"][value="${CSS.escape(input.value)}"]`;
      for (const other of document.querySelectorAll(selector)) {
        /** @type {HTMLInputElement} */ (other).checked = input.checked;
      }
    }
  }

  /**
   * Sets the checked state of every input from the URL. Needed because Liquid can't read these
   * parameters, so a re-rendered filter panel always comes back unchecked.
   */
  #restoreInputs() {
    const active = this.#activeValues();

    for (const input of document.querySelectorAll('title-facet-inputs input[type="checkbox"]')) {
      if (!(input instanceof HTMLInputElement)) continue;
      input.checked = active[input.name]?.has(input.value) ?? false;
    }
  }

  /**
   * Updates the count bubble in each group's summary, and the visibility of its clear button.
   */
  #updateSummaries() {
    for (const component of document.querySelectorAll('title-facet-inputs')) {
      const checked = Array.from(component.querySelectorAll('input[type="checkbox"]:checked')).filter(
        (input) => input instanceof HTMLInputElement
      );

      const status = component.closest('details')?.querySelector('facet-status-component');
      if (status && 'updateListSummary' in status && typeof status.updateListSummary === 'function') {
        status.updateListSummary(checked);
      }

      component.querySelector('.facets__clear')?.classList.toggle('facets__clear--active', checked.length > 0);
    }
  }

  /**
   * @param {number | null} count - The number of matches, or null to restore the server-rendered count
   */
  #renderCount(count) {
    for (const status of document.querySelectorAll('.products-count-wrapper [role="status"]')) {
      if (!(status instanceof HTMLElement)) continue;

      if (count === null) {
        const original = status.dataset.titleFacetOriginal;
        if (original != null) status.textContent = original;
        continue;
      }

      status.dataset.titleFacetOriginal ??= status.textContent?.trim() ?? '';

      const template = count === 1 ? this.#translations.item_count_one : this.#translations.item_count_other;
      status.textContent = template ? template.replace(/\d+/, String(count)) : String(count);
    }
  }

  /**
   * @param {boolean} show - Whether to show the "no products found" message
   */
  #renderEmptyState(show) {
    const grid = this.#grid;
    const existing = grid?.parentElement?.querySelector('[data-title-facet-empty]');

    if (!show || !grid) {
      existing?.remove();
      return;
    }

    if (existing) return;

    const wrapper = document.createElement('div');
    wrapper.className = 'main-collection-grid__empty';
    wrapper.setAttribute('data-title-facet-empty', '');

    const heading = document.createElement('h2');
    heading.className = 'main-collection-grid__empty-title h2';
    heading.textContent = this.#translations.no_products_found ?? '';

    wrapper.append(heading);
    grid.after(wrapper);
  }

  /** @returns {Record<string, string | undefined>} */
  get #translations() {
    const element = document.querySelector('[data-title-facet-translations]');
    if (!(element instanceof HTMLElement)) return {};

    try {
      return JSON.parse(element.textContent ?? '{}');
    } catch {
      return {};
    }
  }

  /** @returns {Element | null} */
  get #resultsList() {
    return document.querySelector('results-list');
  }

  /** @returns {HTMLElement | null} */
  get #grid() {
    const grid = this.#resultsList?.querySelector('[ref="grid"]');
    return grid instanceof HTMLElement ? grid : null;
  }

  #release() {
    // Let the mutation records from our own writes drain before we listen again.
    setTimeout(() => {
      this.#busy = false;
    }, 0);
  }

  /**
   * A native facet change re-renders the section, which wipes both our checked state and our
   * injected cards. Wait for the new grid to land, then re-apply. Mirrors the approach in
   * `paginated-list.js`.
   */
  #handleFilterUpdate = () => {
    this.#renderObserver?.disconnect();

    const grid = this.#grid;
    if (!grid) return;

    this.#renderObserver = new MutationObserver(() => {
      if (this.#busy) return;

      this.#renderObserver?.disconnect();
      this.scheduleSync();
    });

    this.#renderObserver.observe(grid, {
      childList: true,
      attributes: true,
      attributeFilter: ['data-last-page'],
    });

    // The morph may leave the grid untouched (identical result set), so re-apply regardless.
    setTimeout(() => {
      this.#renderObserver?.disconnect();
      this.scheduleSync();
    }, 600);
  };
}

const titleFacets = new TitleFacets();

/**
 * The checkbox group for a single title-derived facet.
 *
 * @extends Component<{ facetInputs?: HTMLInputElement[] }>
 */
class TitleFacetInputs extends Component {
  connectedCallback() {
    super.connectedCallback();
    titleFacets.scheduleSync();
  }

  apply() {
    titleFacets.commit(this);
  }

  /**
   * @param {Event} event - The click event
   */
  clear(event) {
    event.preventDefault();

    for (const input of this.querySelectorAll('input[type="checkbox"]')) {
      /** @type {HTMLInputElement} */ (input).checked = false;
    }

    titleFacets.commit(this);
  }
}

if (!customElements.get('title-facet-inputs')) {
  customElements.define('title-facet-inputs', TitleFacetInputs);
}
